import { cn } from "app/lib/utils"
import { FileVariantType } from "db/enums"
import { motion, useReducedMotion } from "framer-motion"
import { useAtomValue, useSetAtom } from "jotai"
import { Toolbar } from "radix-ui"
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react"
import { PlaybackSpeedType } from "app/player/consts"
import type { PlayerVolumeColor } from "app/player/getVolumeIcon"
import {
  beginAtom,
  currentDurationAtom,
  currentOpenPopoverAtom,
  currentTimeAtom,
  endAtom,
  isFinishedAtom,
} from "app/player/state"
import {
  CONTROL_ROW_HEIGHT_CLASS,
  type PlayerControlSize,
} from "app/player/controlSizes"
import { useCompactNavigation } from "web/src/slides/hooks/useCompactNavigation"
import PlayerFullScreenButton from "./PlayerFullScreenButton"
import PlayerLoopButton from "./PlayerLoopButton"
import {
  type PlayerChipVariant,
  PlayerScrubPlaytime,
  PlayerStaticPlaytime,
} from "./PlayerPlaytime"
import PlayerProgressBar from "./PlayerProgressBar"
import PlayerSkipButton from "./PlayerSkipButton"
import PlayerSlideTitle from "./PlayerSlideTitle"
import VideoRoundPlayButton from "./VideoRoundPlayButton"
import VideoSquarePlayButton from "./VideoSquarePlayButton"
import PlayerQualityDropDown from "./PlayerQualityDropDown"
import PlayerSpeedDropDown from "./PlayerSpeedDropDown"
import PlayerVideoIssueModal from "./PlayerVideoIssueModal"
import PlayerVolumeControl from "./PlayerVolumeControl"

type Props = {
  controlsVisible: boolean
  volume: number
  onVolumeChange: (volume: number) => void
  onProgressValueChange: (value: number[]) => void
  bufferedAmount?: TimeRanges
  onTogglePlayback: () => void
  onReplayClick: () => void
  onSkipBack: () => void
  onSkipForward: () => void
  onFullscreenClick: () => void
  selectedPlaybackSpeed: PlaybackSpeedType
  setPlaybackSpeed: Dispatch<SetStateAction<PlaybackSpeedType>>
  isPublicLesson: boolean
  videoId: number
  selectedQuality: FileVariantType
  sortedVariants: { type: FileVariantType }[]
  onQualityChange: (option: FileVariantType) => void
  slideTitleLabel1?: string | null
  slideTitleLabel2?: string | null
  size?: PlayerControlSize
  /** Toolbar button color. Defaults to blue (video overlay). */
  color?: PlayerVolumeColor
  /** Title / playtime chip variant. Defaults to dark (video overlay). */
  variant?: PlayerChipVariant
}

const controlsOverlayClassName =
  "pointer-events-none absolute inset-0 flex flex-col"

const compactControlsGradientStyle = {
  backgroundImage:
    "linear-gradient(180deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 20%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0.7) 100%)",
}

const desktopControlsGradientStyle = {
  backgroundImage:
    "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%)",
}

const toolbarGroupClassName = "flex items-center gap-1.5"

const CONTROLS_TRANSITION = {
  duration: 0.25,
  ease: "easeOut" as const,
}

const scrimVariants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
}

const topControlsVariants = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: -8 },
}

const middleControlsVariants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
}

const bottomControlsVariants = {
  visible: { opacity: 1, y: 0 },
  hidden: { opacity: 0, y: 16 },
}

function useControlsMotionTransition() {
  const shouldReduceMotion = useReducedMotion()

  return shouldReduceMotion ? { duration: 0 } : CONTROLS_TRANSITION
}

function useControlsMotionVariants() {
  const shouldReduceMotion = useReducedMotion()

  if (!shouldReduceMotion) {
    return {
      scrim: scrimVariants,
      top: topControlsVariants,
      middle: middleControlsVariants,
      bottom: bottomControlsVariants,
    }
  }

  return {
    scrim: scrimVariants,
    top: {
      visible: { opacity: 1, y: 0 },
      hidden: { opacity: 0, y: 0 },
    },
    middle: middleControlsVariants,
    bottom: {
      visible: { opacity: 1, y: 0 },
      hidden: { opacity: 0, y: 0 },
    },
  }
}

function CompactVideoPlayerControls({
  controlsVisible,
  volume,
  onVolumeChange,
  onProgressValueChange,
  bufferedAmount,
  onTogglePlayback,
  onReplayClick,
  onSkipBack,
  onSkipForward,
  onFullscreenClick,
  selectedPlaybackSpeed,
  setPlaybackSpeed,
  isPublicLesson,
  videoId,
  selectedQuality,
  sortedVariants,
  onQualityChange,
  slideTitleLabel1,
  slideTitleLabel2,
  size = "md",
  color = "blue",
  variant = "dark",
}: Props) {
  const isFinished = useAtomValue(isFinishedAtom)
  const currentTime = useAtomValue(currentTimeAtom)
  const begin = useAtomValue(beginAtom)
  const end = useAtomValue(endAtom)
  const currentDuration = useAtomValue(currentDurationAtom)
  const isAtStart = Math.round(currentTime) === Math.round(begin ?? 0)
  const [isScrubbing, setIsScrubbing] = useState(false)
  const setOpenMenu = useSetAtom(currentOpenPopoverAtom)

  const min = begin || 0
  const max = end || currentDuration || 0
  const progress = max > min ? (currentTime - min) / (max - min) : 0

  const handleScrubStart = () => {
    setIsScrubbing(true)
    setOpenMenu(null)
  }

  const handleScrubEnd = () => {
    setIsScrubbing(false)
  }

  const transition = useControlsMotionTransition()
  const variants = useControlsMotionVariants()
  const showTopAndMiddle = controlsVisible && !isScrubbing
  // Defer pointer-events until the show animation finishes so the touch that
  // reveals controls cannot also activate play/skip (ghost click).
  const [controlsInteractive, setControlsInteractive] =
    useState(controlsVisible)
  const controlsVisibleRef = useRef(controlsVisible)
  controlsVisibleRef.current = controlsVisible

  useEffect(() => {
    if (!controlsVisible) {
      setControlsInteractive(false)
    }
  }, [controlsVisible])

  const handleControlsAnimationComplete = () => {
    if (controlsVisibleRef.current) {
      setControlsInteractive(true)
    }
  }

  const topAndMiddleInteractive = controlsInteractive && !isScrubbing
  const scrubTransition = { duration: 0 }
  const topAndMiddleTransition = isScrubbing ? scrubTransition : transition
  const isShortControls = size === "sm"
  const controlRowHeightClass = CONTROL_ROW_HEIGHT_CLASS[size]
  const skipSize = isShortControls ? "md" : "lg"

  return (
    <div className={cn(controlsOverlayClassName, "px-3 pt-3 pb-5")}>
      <motion.div
        aria-hidden
        className="absolute inset-0"
        style={compactControlsGradientStyle}
        variants={variants.scrim}
        initial="hidden"
        animate={controlsVisible ? "visible" : "hidden"}
        transition={transition}
      />
      <div className="relative flex min-h-0 flex-1 flex-col justify-between">
        <motion.div
          className={cn(
            "pointer-events-auto relative z-10 flex items-start justify-between gap-3",
            !topAndMiddleInteractive && "pointer-events-none"
          )}
          variants={variants.top}
          initial="hidden"
          animate={showTopAndMiddle ? "visible" : "hidden"}
          transition={topAndMiddleTransition}
        >
          <PlayerSlideTitle
            label1={slideTitleLabel1}
            label2={slideTitleLabel2}
            variant={variant}
          />
          <Toolbar.Root
            className={toolbarGroupClassName}
            aria-label="Video settings"
          >
            <PlayerLoopButton size={size} color={color} />
            <PlayerSpeedDropDown
              setOption={setPlaybackSpeed}
              selectedOption={selectedPlaybackSpeed}
              size={size}
              triggerColor={color}
            />
            {!isPublicLesson && (
              <PlayerVideoIssueModal
                videoId={videoId}
                selectedQuality={selectedQuality}
                size={size}
                color={color}
              />
            )}
            {sortedVariants.length > 0 && (
              <PlayerQualityDropDown
                options={sortedVariants}
                setOption={onQualityChange}
                selectedOption={selectedQuality}
                size={size}
                triggerColor={color}
              />
            )}
          </Toolbar.Root>
        </motion.div>

        <motion.div
          className={cn(
            "pointer-events-auto relative z-30 flex flex-col gap-0",
            !controlsInteractive && "pointer-events-none"
          )}
          variants={variants.bottom}
          initial="hidden"
          animate={controlsVisible ? "visible" : "hidden"}
          transition={transition}
          onAnimationComplete={handleControlsAnimationComplete}
        >
          <div
            className={cn(
              "flex items-end justify-between gap-3",
              controlRowHeightClass
            )}
          >
            {!isScrubbing && (
              <PlayerStaticPlaytime
                className={controlRowHeightClass}
                variant={variant}
              />
            )}
            <Toolbar.Root
              className={cn(
                toolbarGroupClassName,
                isScrubbing && "pointer-events-none opacity-0"
              )}
              aria-label="Fullscreen and volume controls"
            >
              <PlayerVolumeControl
                volume={volume}
                onVolumeChange={onVolumeChange}
                orientation="horizontal"
                color={color}
                defaultExpanded={false}
                controlsVisible={controlsVisible}
                controlsHideDelayMs={transition.duration * 1000}
                size={size}
              />
              <PlayerFullScreenButton
                size={size}
                color={color}
                onClick={onFullscreenClick}
              />
            </Toolbar.Root>
          </div>
          <div className="relative pt-5 pb-2">
            {isScrubbing && (
              <PlayerScrubPlaytime progress={progress} variant={variant} />
            )}
            <PlayerProgressBar
              onValueChange={onProgressValueChange}
              bufferedAmount={bufferedAmount}
              onScrubStart={handleScrubStart}
              onScrubEnd={handleScrubEnd}
            />
          </div>
        </motion.div>
      </div>

      <motion.div
        className={cn(
          "pointer-events-none absolute inset-0 z-20 flex items-center justify-center",
          !topAndMiddleInteractive && "pointer-events-none"
        )}
        variants={variants.middle}
        initial="hidden"
        animate={showTopAndMiddle ? "visible" : "hidden"}
        transition={topAndMiddleTransition}
      >
        <Toolbar.Root
          className={cn(
            "pointer-events-auto flex items-center justify-center",
            isShortControls ? "gap-6" : "gap-10",
            !topAndMiddleInteractive && "pointer-events-none"
          )}
          aria-label="Playback controls"
        >
          <PlayerSkipButton
            size={skipSize}
            color={color}
            direction="back"
            onClick={onSkipBack}
            disabled={isAtStart}
          />
          <VideoRoundPlayButton
            size={size}
            color={color === "white" ? "white" : "neutraal"}
            onClick={onTogglePlayback}
            onReplayClick={onReplayClick}
          />
          <PlayerSkipButton
            size={skipSize}
            color={color}
            direction="forward"
            onClick={onSkipForward}
            disabled={isFinished}
          />
        </Toolbar.Root>
      </motion.div>
    </div>
  )
}

function DesktopVideoPlayerControls({
  controlsVisible,
  volume,
  onVolumeChange,
  onProgressValueChange,
  bufferedAmount,
  onTogglePlayback,
  onReplayClick,
  onSkipBack,
  onSkipForward,
  onFullscreenClick,
  selectedPlaybackSpeed,
  setPlaybackSpeed,
  isPublicLesson,
  videoId,
  selectedQuality,
  sortedVariants,
  onQualityChange,
  slideTitleLabel1,
  slideTitleLabel2,
  size = "md",
  color = "blue",
  variant = "dark",
}: Props) {
  const isFinished = useAtomValue(isFinishedAtom)
  const currentTime = useAtomValue(currentTimeAtom)
  const begin = useAtomValue(beginAtom)
  const isAtStart = Math.round(currentTime) === Math.round(begin ?? 0)
  const transition = useControlsMotionTransition()
  const variants = useControlsMotionVariants()
  const playtimeHeightClass = CONTROL_ROW_HEIGHT_CLASS[size]

  return (
    <div className={cn(controlsOverlayClassName, "overflow-hidden")}>
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 top-[55%] z-0"
        style={desktopControlsGradientStyle}
        variants={variants.scrim}
        initial="hidden"
        animate={controlsVisible ? "visible" : "hidden"}
        transition={transition}
      />
      <div className="relative z-10 flex min-h-0 flex-1 flex-col px-6 pt-5 pb-5">
        <motion.div
          className={cn(
            "pointer-events-auto",
            !controlsVisible && "pointer-events-none"
          )}
          variants={variants.scrim}
          initial="hidden"
          animate={controlsVisible ? "visible" : "hidden"}
          transition={transition}
        >
          <PlayerSlideTitle
            label1={slideTitleLabel1}
            label2={slideTitleLabel2}
            variant={variant}
          />
        </motion.div>

        <motion.div
          className={cn(
            "pointer-events-auto relative mt-auto flex flex-col",
            !controlsVisible && "pointer-events-none"
          )}
          variants={variants.bottom}
          initial="hidden"
          animate={controlsVisible ? "visible" : "hidden"}
          transition={transition}
        >
          <div className="relative pb-1 pt-5">
            <PlayerProgressBar
              onValueChange={onProgressValueChange}
              bufferedAmount={bufferedAmount}
            />
          </div>

          <div className="relative mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Toolbar.Root
                className={toolbarGroupClassName}
                aria-label="Playback controls"
              >
                <VideoSquarePlayButton
                  size={size}
                  color={color}
                  onClick={onTogglePlayback}
                  onReplayClick={onReplayClick}
                />
                <PlayerSkipButton
                  size={size}
                  color={color}
                  direction="back"
                  onClick={onSkipBack}
                  disabled={isAtStart}
                />
                <PlayerSkipButton
                  size={size}
                  color={color}
                  direction="forward"
                  onClick={onSkipForward}
                  disabled={isFinished}
                />
                <PlayerVolumeControl
                  volume={volume}
                  onVolumeChange={onVolumeChange}
                  orientation="horizontal"
                  color={color}
                  defaultExpanded
                  size={size}
                />
              </Toolbar.Root>
              <PlayerStaticPlaytime
                className={cn(playtimeHeightClass, "rounded-3xl px-4 py-2")}
                variant={variant}
              />
            </div>

            <Toolbar.Root
              className={toolbarGroupClassName}
              aria-label="Video settings"
            >
              <PlayerLoopButton size={size} color={color} />
              <PlayerSpeedDropDown
                setOption={setPlaybackSpeed}
                selectedOption={selectedPlaybackSpeed}
                size={size}
                triggerColor={color}
              />
              {/* NOTE: VideoIssueModal uses useCurrentUser, so we can't render it on public lessons */}
              {!isPublicLesson && (
                <PlayerVideoIssueModal
                  videoId={videoId}
                  selectedQuality={selectedQuality}
                  size={size}
                  color={color}
                />
              )}
              {sortedVariants.length > 0 && (
                <PlayerQualityDropDown
                  options={sortedVariants}
                  setOption={onQualityChange}
                  selectedOption={selectedQuality}
                  size={size}
                  triggerColor={color}
                />
              )}
              <PlayerFullScreenButton
                size={size}
                color={color}
                onClick={onFullscreenClick}
              />
            </Toolbar.Root>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default function VideoPlayerControls(props: Props) {
  const isCompact = useCompactNavigation()

  if (isCompact) {
    return <CompactVideoPlayerControls {...props} />
  }

  return <DesktopVideoPlayerControls {...props} />
}
